import { html, nothing } from "lit";
import type { AppViewState } from "../app-view-state.ts";
import { activeFleetQuestionGlobal } from "../app-fleet-interaction.ts";
import { isFleetQuestionDraftComplete } from "../controllers/fleet-question.ts";

function formatRemaining(ms: number): string {
  const remaining = Math.max(0, ms);
  const totalSeconds = Math.floor(remaining / 1000);
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }
  const minutes = Math.floor(totalSeconds / 60);
  return `${minutes}m`;
}

/**
 * Global fleet question overlay (any non-chat tab) — mirrors exec-approval visibility.
 * Uses the base `.exec-approval-overlay` fixed positioning; do not nest under `.shell--chat`
 * or fleet-theme will demote it to a relative composer card.
 *
 * On the chat tab the inline chat-row renderer (`renderFleetQuestionInline` inside
 * `.chat-thread`) is used instead — the operator answers in the chat itself.
 */
export function renderGlobalFleetQuestionPrompt(state: AppViewState) {
  if (state.tab === "chat") {
    return nothing;
  }
  const active = activeFleetQuestionGlobal(state);
  if (!active) {
    return nothing;
  }
  const remainingMs = active.expiresAtMs - Date.now();
  const remaining = remainingMs > 0 ? `expires in ${formatRemaining(remainingMs)}` : "expired";
  const otherSession =
    active.sessionKey.trim() !== state.sessionKey.trim() ? `Session: ${active.sessionKey}` : null;
  const canSubmit =
    isFleetQuestionDraftComplete(active, state.fleetQuestionDraft, state.fleetQuestionFreeText) &&
    !state.fleetQuestionBusy;
  const queueCount = state.fleetQuestionQueue.length;

  return html`
    <div
      class="exec-approval-overlay"
      role="dialog"
      aria-live="assertive"
      aria-label="Fleet question"
    >
      <div class="exec-approval-card fleet-question-card">
        <div class="exec-approval-header">
          <div>
            <div class="exec-approval-title">${active.title}</div>
            <div class="exec-approval-sub">
              ${remaining}${otherSession ? ` · ${otherSession}` : ""}
            </div>
          </div>
          ${
            queueCount > 1
              ? html`<div class="exec-approval-queue">${queueCount} pending</div>`
              : nothing
          }
        </div>
        <div class="fleet-question-body">
          ${active.questions.map(
            (q) => html`
              <fieldset class="fleet-question-fieldset">
                <legend class="fleet-question-prompt">${q.prompt}</legend>
                <div class="fleet-question-options" role="group" aria-label=${q.prompt}>
                  ${q.options.map((opt) => {
                    const selected = (state.fleetQuestionDraft[q.id] ?? []).includes(opt.id);
                    return html`
                      <label
                        class="fleet-question-option ${selected ? "fleet-question-option--selected" : ""}"
                      >
                        <input
                          type=${q.allowMultiple ? "checkbox" : "radio"}
                          name=${`fleet-global-q-${active.id}-${q.id}`}
                          .checked=${selected}
                          ?disabled=${state.fleetQuestionBusy}
                          @change=${() =>
                            state.handleFleetQuestionToggleOption(
                              q.id,
                              opt.id,
                              q.allowMultiple === true,
                            )}
                        />
                        <span>${opt.label}</span>
                      </label>
                    `;
                  })}
                </div>
                <label class="fleet-question-other muted">
                  <span>Other (optional)</span>
                  <input
                    type="text"
                    class="fleet-question-other-input"
                    .value=${state.fleetQuestionFreeText[q.id] ?? ""}
                    ?disabled=${state.fleetQuestionBusy}
                    placeholder="Free-text answer"
                    @input=${(e: Event) =>
                      state.handleFleetQuestionFreeTextChange(
                        q.id,
                        (e.target as HTMLInputElement).value,
                      )}
                  />
                </label>
              </fieldset>
            `,
          )}
        </div>
        ${
          state.fleetQuestionError
            ? html`<div class="exec-approval-error">${state.fleetQuestionError}</div>`
            : nothing
        }
        <div class="exec-approval-actions">
          <button
            class="btn primary"
            type="button"
            ?disabled=${!canSubmit}
            @click=${() => void state.handleFleetQuestionSubmit()}
          >
            Continue
          </button>
        </div>
        ${
          otherSession
            ? html`
                <div class="exec-approval-sub" style="margin-top: 8px">
                  Answering applies to the session above (may differ from the tab you are viewing).
                </div>
              `
            : nothing
        }
      </div>
    </div>
  `;
}
